import PropTypes from "prop-types";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useWatch } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Spinner } from "@/components/ui/spinner";
import { BaseForm, FormMultiSelect } from "components/BaseForm";
import { filterUsersForDropdown } from "helpers/user";
import TwilioPhoneNumberService from "services/TwilioPhoneNumber";
import UserService from "services/User";

const schema = z.object({
  user_ids: z.array(z.number()),
});

const AgentsField = ({ users, defaultUserIds }) => {
  const selected = useWatch({ name: "user_ids" });
  // Keep currently-selected users visible in the option list even when they're
  // deactivated, so operators can un-assign them but can't newly pick one.
  const options = useMemo(
    () => filterUsersForDropdown(users, selected ?? defaultUserIds ?? []),
    [users, selected, defaultUserIds]
  );

  return (
    <FormMultiSelect
      name="user_ids"
      label="Agents"
      options={options}
      placeholder="Select agents"
    />
  );
};

AgentsField.propTypes = {
  users: PropTypes.array.isRequired,
  defaultUserIds: PropTypes.arrayOf(PropTypes.number),
};

const AssignAgentsForm = ({ twilioPhoneNumberIds, userIds, onSuccess }) => {
  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => UserService.getUsers(),
  });

  if (isLoading) {
    return (
      <div className="tw:flex tw:items-center tw:justify-center tw:p-6">
        <Spinner />
      </div>
    );
  }

  const handleSubmit = async ({ user_ids }) => {
    await TwilioPhoneNumberService.assignAgents(twilioPhoneNumberIds, user_ids);
  };

  const handleSuccess = () => {
    toast.success("Agents assigned");
    onSuccess?.();
  };

  return (
    <BaseForm
      schema={schema}
      defaultValues={{ user_ids: userIds ?? [] }}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      submitButtonText="Save"
    >
      <AgentsField users={users} defaultUserIds={userIds} />
    </BaseForm>
  );
};

AssignAgentsForm.propTypes = {
  twilioPhoneNumberIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  userIds: PropTypes.arrayOf(PropTypes.number),
  onSuccess: PropTypes.func.isRequired,
};

export default AssignAgentsForm;
